import React from "react";
import { useIntl } from "react-intl";
import type { AncEncounter } from "../../../../__generated__/base.gql";
import { isIptpSpVmpId } from "../../../patient/[:patientId]/antenatal-care/_lib/iptp-sp-vmp-ids";
import { useElephant200AdditionsFlag } from "../../../patient/[:patientId]/antenatal-care/_lib/use-elephant-2-00-additions-flag";
import { isDefined } from "../../_lib/is-defined";
import { RegisterTable } from "../../_lib/register-table";
import useTimeZone from "../../_lib/use-time-zone";
import type { AncRegisterQuery } from "../__generated__/page.gql";
import { createRowFormatter } from "./create-row-formatter";
import { getViewColumns } from "./get-view-columns";

export const AncRegisterTable = ({
  currentPage,
  setCurrentPage,
  loading,
  data,
}: {
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  loading: boolean;
  data?: AncRegisterQuery;
}) => {
  const { formatMessage } = useIntl();
  const timeZone = useTimeZone();
  const elephant200AdditionsFlagOn = useElephant200AdditionsFlag();

  /**
   * IPTp/SP dose counts per patient, computed from the encounters in view.
   * Limitation: only counts doses captured in the visible date range, so
   * widening the range shows a more complete cumulative number. A
   * server-resolved per-encounter dose count would remove this caveat.
   *
   * If any encounter for the patient carries a clinician override, that
   * override wins over the auto-count.
   */
  const iptpDoseByPatient = React.useMemo(() => {
    const autoCounts = new Map<string, number>();
    const latestOverride = new Map<
      string,
      { value: number; createdAt: number }
    >();
    for (const encounter of data?.ancEncounters.encounters.filter(isDefined) ??
      []) {
      const patientUuid = encounter.patient?.uuid;
      if (!patientUuid) continue;
      const doses =
        encounter.prescriptions?.filter((p) =>
          isIptpSpVmpId(p?.clinicalProduct?.id),
        ).length ?? 0;
      autoCounts.set(patientUuid, (autoCounts.get(patientUuid) ?? 0) + doses);
      if (encounter.iptpDoseOverride != null) {
        const createdAt = new Date(encounter.createdAt).getTime();
        const prev = latestOverride.get(patientUuid);
        if (prev == null || createdAt > prev.createdAt) {
          latestOverride.set(patientUuid, {
            value: encounter.iptpDoseOverride,
            createdAt,
          });
        }
      }
    }
    const merged = new Map<string, number>();
    for (const [patient, auto] of autoCounts) {
      merged.set(patient, latestOverride.get(patient)?.value ?? auto);
    }
    for (const [patient, override] of latestOverride) {
      if (!merged.has(patient)) merged.set(patient, override.value);
    }
    return merged;
  }, [data]);

  const rowFormatter = createRowFormatter(
    timeZone,
    formatMessage,
    iptpDoseByPatient,
  );

  return (
    <RegisterTable
      errorBoundaryName="anc-register-table"
      currentPage={currentPage}
      setCurrentPage={setCurrentPage}
      loading={loading}
      columns={getViewColumns(formatMessage, { elephant200AdditionsFlagOn })}
      rows={
        data?.ancEncounters.encounters
          .filter(isDefined)
          .map((e) => rowFormatter(e as AncEncounter)) || []
      }
      totalRows={data?.ancEncounters.total || 0}
    />
  );
};
